import { getAccessToken } from '@privy-io/react-auth';
import { PublicKey, VersionedTransaction, Transaction } from '@solana/web3.js';
import { IWallet } from '@velocity-exchange/sdk';
import Env from 'src/environmentVariables/EnvironmentVariables';
import { FeePayerError } from 'src/constants/errors/FeePayerError';
import { TimingCallbacks } from './IWalletV2';

const FEE_PAYER_PUBLIC_KEY = process.env.NEXT_PUBLIC_FEE_PAYER_PUBLIC_KEY;

if (!FEE_PAYER_PUBLIC_KEY) {
	throw new Error('FEE_PAYER_PUBLIC_KEY is not set');
}

export const feePayerSignTransaction = async (
	tx: Transaction | VersionedTransaction,
	timingCallbacks?: Pick<
		TimingCallbacks,
		'onFeePayerSigningStart' | 'onFeePayerSigningEnd'
	>
) => {
	try {
		timingCallbacks?.onFeePayerSigningStart?.();
		const privyAccessToken = await getAccessToken();

		if (!privyAccessToken) {
			throw new Error('No access token');
		}

		// @ts-ignore
		const serializedTransaction = Buffer.from(tx.serialize()).toString(
			'base64'
		);
		const res = await fetch(`${Env.dataApiUrl}/tx/fee/sign`, {
			method: 'POST',
			headers: {
				Authorization: `Bearer ${privyAccessToken}`,
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({
				serializedTransaction,
			}),
		});

		if (!res.ok) {
			throw new FeePayerError(
				`Fee payer service returned ${res.status}`,
				await res.text()
			);
		}

		const { tx: serializedSignedTransaction } = await res.json();

		const transactionBuffer = Buffer.from(
			serializedSignedTransaction,
			'base64'
		);
		const versionedTransaction = VersionedTransaction.deserialize(
			transactionBuffer as unknown as Uint8Array
		);

		timingCallbacks?.onFeePayerSigningEnd?.();
		return versionedTransaction;
	} catch (e) {
		timingCallbacks?.onFeePayerSigningEnd?.();

		if (e instanceof FeePayerError) {
			throw e;
		}

		throw new FeePayerError('Fee payer failed to sign transaction', e);
	}
};

export const FEE_PAYER_WALLET: IWallet = {
	publicKey: new PublicKey(FEE_PAYER_PUBLIC_KEY),

	// @ts-ignore - Allow return of VersionedTransaction
	signTransaction: feePayerSignTransaction,

	// @ts-ignore - Allow return of VersionedTransaction[]
	signAllTransactions: async (txs: (Transaction | VersionedTransaction)[]) => {
		return Promise.all(txs.map((tx) => feePayerSignTransaction(tx)));
	},
};
